import React, { useMemo, useState } from "react";
import { Printer, X, Info } from "lucide-react";
import logoMark from "../assets/logo-mark.png";

// Detect the production URL from env when available; otherwise fall back to
// the browser's current origin so admins can still print from preview.
const defaultAppUrl = () => {
  const envUrl = process.env.REACT_APP_PRODUCTION_URL;
  if (envUrl) return envUrl.replace(/^https?:\/\//i, "").replace(/\/$/, "");
  if (typeof window !== "undefined") {
    return window.location.host + (window.location.pathname === "/" ? "" : window.location.pathname);
  }
  return "400club.redhookelks.org";
};

// A single 5.5"×8.5" printable card. Renders once inside a fixed print-only
// container that hides the rest of the app when window.print() is invoked.
const PrintCardBody = ({ url, name, email, password, role }) => {
  const isVolunteer = role === "checkin_volunteer";
  return (
    <div
      className="print-login-card"
      style={{
        width: "5.5in",
        minHeight: "8.5in",
        padding: "0.5in",
        boxSizing: "border-box",
        background: "#FFFFFF",
        color: "#0B1214",
        fontFamily: "'Public Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        display: "flex",
        flexDirection: "column",
        gap: "0.35in",
      }}
    >
      {/* Header */}
      <div style={{ textAlign: "center" }}>
        <img
          src={logoMark}
          alt="Red Hook Elks Lodge"
          style={{ height: "0.7in", width: "auto", display: "inline-block" }}
        />
        <div style={{ marginTop: "0.15in", fontFamily: "'Lora', ui-serif, Georgia, serif", fontSize: "22pt", fontWeight: 700, lineHeight: 1.1 }}>
          400 Club — Drawing Check-In
        </div>
        <div style={{ marginTop: "0.05in", fontSize: "11pt", color: "#3D4B4E" }}>
          Red Hook Rhinebeck Elks Lodge #2022
        </div>
      </div>

      {/* Credentials block */}
      <div style={{ border: "2px solid #122E36", borderRadius: "8px", padding: "0.3in", background: "#F5F1E9" }}>
        <div style={{ fontSize: "9pt", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#657174" }}>
          Sign in at
        </div>
        <div style={{ marginTop: "0.05in", fontFamily: "'IBM Plex Mono', ui-monospace, SFMono-Regular, monospace", fontSize: "16pt", fontWeight: 600, color: "#122E36", wordBreak: "break-all" }}>
          {url || "—"}
        </div>

        <div style={{ marginTop: "0.25in", display: "grid", gridTemplateColumns: "1.15in 1fr", rowGap: "0.15in", columnGap: "0.15in", fontSize: "12pt" }}>
          <div style={{ fontWeight: 700 }}>Name:</div>
          <div style={{ borderBottom: "1px dashed #9AA3A4", paddingBottom: "2px" }}>{name || " "}</div>

          <div style={{ fontWeight: 700 }}>Email:</div>
          <div style={{ borderBottom: "1px dashed #9AA3A4", paddingBottom: "2px", fontFamily: "'IBM Plex Mono', monospace", fontSize: "11pt" }}>{email || " "}</div>

          <div style={{ fontWeight: 700 }}>Password:</div>
          <div style={{ borderBottom: "1px dashed #9AA3A4", paddingBottom: "2px", fontFamily: "'IBM Plex Mono', monospace", fontSize: "13pt", letterSpacing: "0.03em" }}>
            {password || <span style={{ color: "#9AA3A4", fontStyle: "italic" }}>__________________________</span>}
          </div>
        </div>
      </div>

      {/* Quick steps */}
      <div>
        <div style={{ fontSize: "10pt", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#657174", marginBottom: "0.1in" }}>
          Quick guide
        </div>
        <ol style={{ paddingLeft: "0.25in", margin: 0, fontSize: "11.5pt", lineHeight: 1.5 }}>
          <li>Open the URL above and sign in with the email &amp; password.</li>
          {isVolunteer ? (
            <li>You&rsquo;ll land on the <strong>Check-In</strong> screen automatically.</li>
          ) : (
            <li>Tap the <strong>Check-In</strong> tab in the top navigation.</li>
          )}
          <li>Pick tonight&rsquo;s drawing from the dropdown.</li>
          <li>
            Ask each arriving ticket holder for their <strong>ticket #</strong> or name,
            type it in the search box, and press <strong>Enter</strong>.
          </li>
          <li>
            When the pop-up appears, ask <strong>&ldquo;Any guest with you tonight?&rdquo;</strong> &mdash;
            tap <strong>Yes +1 guest</strong> or <strong>No guest</strong>.
          </li>
          <li>The row turns green &mdash; they&rsquo;re checked in. Tap the &#8635; icon to undo a mistake.</li>
        </ol>
      </div>

      {/* Footer tip */}
      <div style={{ marginTop: "auto", borderTop: "1px solid #D9CFBC", paddingTop: "0.15in", fontSize: "9.5pt", color: "#657174" }}>
        <strong style={{ color: "#3D4B4E" }}>Tip:</strong> The screen refreshes every few seconds, so if
        another volunteer checks someone in, you&rsquo;ll see it. Keep the search box focused for the fastest flow.
      </div>
    </div>
  );
};

export const PrintLoginCardModal = ({ user, initialPassword = "", onClose }) => {
  const [password, setPassword] = useState(initialPassword);
  const [url, setUrl] = useState(defaultAppUrl());
  const previewName = user?.name || "";
  const previewEmail = user?.email || "";
  const previewRole = user?.role || "seller";

  const doPrint = () => {
    // Give React time to flush before the browser dialog steals focus.
    setTimeout(() => window.print(), 50);
  };

  const roleLabel = useMemo(() => {
    if (previewRole === "checkin_volunteer") return "Check-in Volunteer";
    if (previewRole === "admin") return "Admin";
    if (previewRole === "viewer") return "Viewer";
    return "Seller";
  }, [previewRole]);

  return (
    <>
      {/* Print-only stylesheet — hides the entire app and prints just the card. */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #print-login-card-root, #print-login-card-root * { visibility: visible !important; }
          #print-login-card-root {
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            width: 100% !important;
            display: block !important;
            background: #fff !important;
          }
          #print-login-card-modal-shell { display: none !important; }
          @page { size: letter; margin: 0.4in; }
        }
      `}</style>

      {/* Print-only render target — invisible on screen; shown only in print. */}
      <div
        id="print-login-card-root"
        style={{ position: "fixed", left: "-10000px", top: 0 }}
        aria-hidden="true"
      >
        <PrintCardBody url={url} name={previewName} email={previewEmail} password={password} role={previewRole} />
      </div>

      {/* On-screen modal with preview + edit controls. */}
      <div
        id="print-login-card-modal-shell"
        data-testid="print-card-modal"
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ background: "rgba(11,18,20,0.55)" }}
        onClick={onClose}
      >
        <div
          className="rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
          style={{ background: "var(--surface-card)", border: "1px solid var(--border-default)" }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between px-5 py-4" style={{ borderBottom: "1px solid var(--border-default)" }}>
            <div>
              <h3 className="text-base font-bold" style={{ color: "var(--ink-primary)" }}>
                Print login card — {previewName} ({roleLabel})
              </h3>
              <p className="mt-0.5 text-xs" style={{ color: "var(--ink-tertiary)" }}>
                A one-page cheat-sheet to tape to the check-in table so this user can sign in fast on drawing night.
              </p>
            </div>
            <button type="button" onClick={onClose} data-testid="print-card-close" className="p-1 rounded" aria-label="Close">
              <X className="h-5 w-5" style={{ color: "var(--ink-tertiary)" }} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {/* Editable fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide block mb-1" style={{ color: "var(--ink-tertiary)" }}>App URL</label>
                <input
                  data-testid="print-card-url"
                  type="text"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--brand-primary)]/25"
                  style={{ borderColor: "var(--border-default)", background: "var(--surface-card)", color: "var(--ink-primary)" }}
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide block mb-1" style={{ color: "var(--ink-tertiary)" }}>Password (optional)</label>
                <input
                  data-testid="print-card-password"
                  type="text"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Leave blank for a fill-in line"
                  className="w-full rounded-md border px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[color:var(--brand-primary)]/25"
                  style={{ borderColor: "var(--border-default)", background: "var(--surface-card)", color: "var(--ink-primary)" }}
                />
              </div>
            </div>

            <div className="flex items-start gap-2 text-xs rounded-md p-2.5" style={{ background: "var(--semantic-warning-soft)", color: "var(--ink-secondary)" }}>
              <Info className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: "var(--semantic-warning)" }} />
              <span>
                Passwords are never shown after they&rsquo;re set. If you don&rsquo;t remember this user&rsquo;s password, either{" "}
                <strong>reset it now</strong> (Sellers tab &rarr; Reset password) or leave the password field blank
                to print with a dashed line and hand-write it in when you deliver the card.
              </span>
            </div>

            {/* Live preview */}
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: "var(--ink-tertiary)" }}>Preview</div>
              <div
                data-testid="print-card-preview"
                className="mx-auto border rounded-md overflow-hidden"
                style={{
                  borderColor: "var(--border-default)",
                  background: "#f7f4ec",
                  transformOrigin: "top center",
                  transform: "scale(0.72)",
                  width: "5.5in",
                  height: "6.2in",
                }}
              >
                <PrintCardBody url={url} name={previewName} email={previewEmail} password={password} role={previewRole} />
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 px-5 py-3" style={{ borderTop: "1px solid var(--border-default)", background: "var(--surface-subtle)" }}>
            <button
              type="button"
              data-testid="print-card-print"
              onClick={doPrint}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md text-sm font-semibold text-white transition-colors hover:opacity-90"
              style={{ background: "var(--brand-primary)" }}
            >
              <Printer className="h-4 w-4" /> Open print dialog
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-md text-sm font-semibold border transition-colors hover:bg-[color:var(--surface-card)]"
              style={{ borderColor: "var(--border-default)", color: "var(--ink-secondary)", background: "var(--surface-card)" }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
